import { Injectable } from '@angular/core';
import { Router, ResolveEnd, ActivatedRouteSnapshot } from '@angular/router';

import { filter } from 'rxjs/operators';

import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';

import { AuthService } from '../auth/services/auth.service';

import { environment } from 'src/environments/environment';

@Injectable({
	providedIn: 'root'
})
export class AppInsightsService {

	private appInsights = new ApplicationInsights({
		config: { instrumentationKey: environment.appInsights.instrumentationKey }
	});

	constructor(router: Router, private authService: AuthService) {
		this.appInsights.loadAppInsights();
		this.initializeUser();

		router.events.pipe(filter(event => event instanceof ResolveEnd)).subscribe((event: ResolveEnd) => {
			const activatedComponent = this.getActivatedComponent(event.state.root);
			if (activatedComponent) {
				this.logPageView(`${activatedComponent.name} ${this.getRouteTemplate(event.state.root)}`, event.urlAfterRedirects);
			}
		});
	}

	public logPageView(name?: string, uri?: string) {
		this.appInsights.trackPageView({ name, uri });
	}

	public trackEvent(name: string, customProperties: { [key: string]: any }): void {
		this.appInsights.trackEvent({ name }, { ...customProperties, appUserId: this.authService.getUserId });
	}

	public trackError(name: string, error: Error, contextDescription?: string, ...params: any[]): void {
		this.appInsights.trackException({
			severityLevel: SeverityLevel.Error,
			error: error,
			properties: {
				name,
				contextDescription,
				appUserId: this.authService.getUserId,
				params
			}
		});
	}

	private getActivatedComponent(snapshot: ActivatedRouteSnapshot): any {
		if (snapshot.firstChild) {
			return this.getActivatedComponent(snapshot.firstChild);
		}

		return snapshot.component;
	}

	private setUserId(userId: string) {
		this.appInsights.setAuthenticatedUserContext(userId);
	}

	private clearUserId() {
		this.appInsights.clearAuthenticatedUserContext();
	}

	private getRouteTemplate(snapshot: ActivatedRouteSnapshot): string {
		let path = '';
		if (snapshot.routeConfig) {
			path += snapshot.routeConfig.path;
		}

		if (snapshot.firstChild) {
			return path + this.getRouteTemplate(snapshot.firstChild);
		}

		return path;
	}

	private initializeUser(): void {

		this.authService.userId$.subscribe(x => {
			if (x && x !== '') {
				this.setUserId(x);
			} else {
				this.clearUserId();
			}
		});

		const userId = this.authService.getUserId;
		if (userId && userId !== '') {
			this.setUserId(userId);
		}
	}
}
